import { addDays, differenceInMinutes, getHours, getMinutes, setHours, setMinutes, startOfMinute } from 'date-fns';
import type { EventVideoConference, EventWebinar, NodeOrganization } from 'models';
import { useMemo } from 'react';
import { getDatePickerDateType } from 'ui';
import type { OrganizationOption } from '../../../components';
import { useFormatTime, useGenerateDefaultHeroImage } from '../../../hooks';
import type { DuplicateEventFormValues } from '../DuplicateEventForm';

type P = {
  event: EventVideoConference | EventWebinar;
  organization?: NodeOrganization;
};

const getOrganizationOption = ({
  organization,
}: {
  organization?: NodeOrganization;
}): OrganizationOption | undefined => {
  if (!organization) {
    return undefined;
  }

  return {
    label: organization.name,
    optionLabel: organization.name,
    originalOrganization: organization,
    value: organization.id,
  };
};

export const useGetInitialValues = ({ event, organization }: P) => {
  const formatTime = useFormatTime();
  const defaultHeroImage = useGenerateDefaultHeroImage();

  return useMemo((): Partial<DuplicateEventFormValues> => {
    const { channel, endTime, html, name, startTime, text, type } = event;
    const tomorrowStartDate = startOfMinute(
      setMinutes(setHours(addDays(new Date(), 1), getHours(startTime)), getMinutes(startTime)),
    );

    return {
      duplicateInviteesFromEvent: true,
      duration: differenceInMinutes(endTime, startTime),
      featuredInChannels: channel?.featuredInChannels,
      heroImage: defaultHeroImage,
      htmlDescription: html,
      name,
      organization: getOrganizationOption({ organization }),
      showInChannels: channel?.showInChannel,
      startDate: getDatePickerDateType(tomorrowStartDate),
      startTime: formatTime(tomorrowStartDate),
      tags: channel?.tags,
      textDescription: text,
      type,
    };
  }, [event, organization, defaultHeroImage, formatTime]);
};
